// src/SuperAuditLog.js
import React, { useEffect, useState } from 'react';
import API from './api';
import { Clock, MapPin, Search } from 'lucide-react';

const roles = [
  { value: '', label: 'All roles' },
  { value: 'super_admin', label: 'Super admin' },
  { value: 'admin', label: 'Org admin' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'parent', label: 'Parent' },
];

const geoTypes = [
  { value: '', label: 'All event types' },
  { value: 'login', label: 'Login' },
  { value: 'arrival', label: 'Arrival' },
  { value: 'checkin', label: 'Check-in' },
  { value: 'checkout', label: 'Check-out' },
  { value: 'other', label: 'Other' },
];

// Helper da formatira Date u "YYYY-MM-DD"
function formatDate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function SuperAuditLog() {
  const [activeTab, setActiveTab] = useState('actions'); // 'actions' | 'geo'

  // Filters za actions tab
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [orgId, setOrgId] = useState('');

  // Filters za geo tab
  const [geoDateFrom, setGeoDateFrom] = useState('');
  const [geoDateTo, setGeoDateTo] = useState('');
  const [geoRoleFilter, setGeoRoleFilter] = useState('');
  const [geoType, setGeoType] = useState('');
  const [geoOrgId, setGeoOrgId] = useState('');
  const [geoQuickRange, setGeoQuickRange] = useState(''); // '', 'today', 'last7', 'last30'

  // Data
  const [actions, setActions] = useState([]);
  const [geoLogs, setGeoLogs] = useState([]);

  // Loading / error
  const [loadingActions, setLoadingActions] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const [errorActions, setErrorActions] = useState('');
  const [errorGeo, setErrorGeo] = useState('');

  // expanded details (da ne pršti ceo details JSON uvek)
  const [expandedId, setExpandedId] = useState(null);

  // MAP MODAL state
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapCoords, setMapCoords] = useState({
    lat: null,
    lon: null,
    userName: '',
    timestamp: '',
    type: '',
    orgName: '',
  });

  // --- COMMON FETCH za GEO (da ne dupliramo logiku) ---
  const fetchGeo = async ({
    date_from,
    date_to,
    user_role,
    geo_type,
    org_id,
  }) => {
    setLoadingGeo(true);
    setErrorGeo('');
    try {
      const res = await API.get('/super/audit-log/geolocation', {
        params: {
          date_from: date_from || undefined,
          date_to: date_to || undefined,
          user_role: user_role || undefined,
          geo_type: geo_type || undefined,
          org_id: org_id || undefined,
        },
      });
      setGeoLogs(res.data || []);
    } catch (e) {
      console.error(e);
      setErrorGeo(
        e?.response?.data?.error || 'Failed to load geolocation logs.'
      );
    } finally {
      setLoadingGeo(false);
    }
  };

  const loadActions = async () => {
    setLoadingActions(true);
    setErrorActions('');
    try {
      const res = await API.get('/super/audit-log/actions', {
        params: {
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          user_role: roleFilter || undefined,
          search: search || undefined,
          org_id: orgId || undefined,
        },
      });
      setActions(res.data || []);
    } catch (e) {
      console.error(e);
      setErrorActions(
        e?.response?.data?.error || 'Failed to load audit log.'
      );
    } finally {
      setLoadingActions(false);
    }
  };

  // Wrapper koji koristi trenutno stanje filtera
  const loadGeo = () =>
    fetchGeo({
      date_from: geoDateFrom,
      date_to: geoDateTo,
      user_role: geoRoleFilter,
      geo_type: geoType,
      org_id: geoOrgId,
    });

  // inicijalno učitavanje
  useEffect(() => {
    loadActions();
    loadGeo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const renderDetailsCell = (log) => {
    const text = log.details || '';
    if (!text) return <span className="text-xs text-gray-400">–</span>;

    const isExpanded = expandedId === log.id;
    const maxLen = 160;

    if (isExpanded || text.length <= maxLen) {
      return (
        <div className="text-xs whitespace-pre-wrap">
          {text}
          {text.length > maxLen && (
            <button
              type="button"
              onClick={() => toggleExpand(log.id)}
              className="ml-2 text-[10px] text-blue-600 underline"
            >
              Show less
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="text-xs">
        {text.slice(0, maxLen)}…
        <button
          type="button"
          onClick={() => toggleExpand(log.id)}
          className="ml-2 text-[10px] text-blue-600 underline"
        >
          Show more
        </button>
      </div>
    );
  };

  // --- MAP helpers ---
  const openMapModal = (log) => {
    if (log.lat == null || log.lon == null) return;
    setMapCoords({
      lat: Number(log.lat),
      lon: Number(log.lon),
      userName: log.user_name || '',
      timestamp: log.timestamp || '',
      type: log.type || '',
      orgName: log.organization_name || '',
    });
    setMapModalOpen(true);
  };

  const closeMapModal = () => {
    setMapModalOpen(false);
  };

  // Pre-calc za OSM embed (koristi se samo ako je modal otvoren i ima coords)
  let embedUrl = '';
  let fullMapUrl = '';
  if (mapModalOpen && mapCoords.lat != null && mapCoords.lon != null) {
    const lat = mapCoords.lat;
    const lon = mapCoords.lon;
    const delta = 0.01; // mali bbox oko tačke
    const left = lon - delta;
    const right = lon + delta;
    const bottom = lat - delta;
    const top = lat + delta;
    const bbox = `${left},${bottom},${right},${top}`;

    embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lon}`;
    fullMapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=16/${lat}/${lon}`;
  }

  // --- QUICK RANGE ZA GEO ---
  const applyGeoQuickRange = (range) => {
    setGeoQuickRange(range);

    const today = new Date();
    const to = formatDate(today);
    let from = '';

    if (range === 'today') {
      from = to;
    } else if (range === 'last7') {
      const d = new Date();
      d.setDate(d.getDate() - 6); // uklj. danas => 7 dana
      from = formatDate(d);
    } else if (range === 'last30') {
      const d = new Date();
      d.setDate(d.getDate() - 29); // uklj. danas => 30 dana
      from = formatDate(d);
    } else {
      // ako range = '' resetujemo samo "quick" state, ne diramo datume
      return;
    }

    setGeoDateFrom(from);
    setGeoDateTo(to);

    // odmah povuci podatke sa ovim range-om + ostalim filterima
    fetchGeo({
      date_from: from,
      date_to: to,
      user_role: geoRoleFilter,
      geo_type: geoType,
      org_id: geoOrgId,
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 text-sm text-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Clock size={16} />
            System Logs
          </h2>
          <p className="text-[11px] text-gray-500">
            High-level overview of user actions and geolocation events across all organizations.
          </p>
        </div>

        <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setActiveTab('actions')}
            className={
              'px-3 py-1.5 ' +
              (activeTab === 'actions'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100')
            }
          >
            User actions
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('geo')}
            className={
              'px-3 py-1.5 ' +
              (activeTab === 'geo'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100')
            }
          >
            Geolocation events
          </button>
        </div>
      </div>

      {/* ACTIONS TAB */}
      {activeTab === 'actions' && (
        <>
          {/* Filters */}
          <div className="mb-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Date from
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border rounded px-2 py-1 text-xs w-full"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Date to
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border rounded px-2 py-1 text-xs w-full"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="border rounded px-2 py-1 text-xs w-full"
              >
                {roles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Org ID (optional)
              </label>
              <input
                type="number"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                className="border rounded px-2 py-1 text-xs w-full"
                placeholder="e.g. 1"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Search
              </label>
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <Search
                    size={12}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border rounded pl-6 pr-2 py-1 text-xs w-full"
                    placeholder="action, user, email, org..."
                  />
                </div>
                <button
                  type="button"
                  onClick={loadActions}
                  className="px-3 py-1 rounded bg-blue-600 text-white text-xs"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          {errorActions && (
            <div className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {errorActions}
            </div>
          )}

          {loadingActions ? (
            <div className="text-xs text-gray-600">Loading actions…</div>
          ) : actions.length === 0 ? (
            <div className="text-xs text-gray-500">
              No audit log entries for the selected filters.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-md overflow-auto max-h-[60vh]">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-1 text-left">Time</th>
                    <th className="px-2 py-1 text-left">Organization</th>
                    <th className="px-2 py-1 text-left">User</th>
                    <th className="px-2 py-1 text-left">Action</th>
                    <th className="px-2 py-1 text-left">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((log) => (
                    <tr
                      key={log.id}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-2 py-1 align-top text-[11px] text-gray-700 whitespace-nowrap">
                        {log.created_at}
                      </td>
                      <td className="px-2 py-1 align-top text-[11px]">
                        <div className="font-semibold text-gray-800">
                          {log.organization_name || '—'}
                        </div>
                        {log.organization_id && (
                          <div className="text-[10px] text-gray-400">
                            ID: {log.organization_id}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1 align-top text-[11px]">
                        <div className="font-semibold text-gray-800">
                          {log.user_name || '—'}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {log.user_email || 'No email'}{' '}
                          {log.user_role && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                              {log.user_role}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1 align-top text-[11px]">
                        <span className="font-semibold text-gray-800">
                          {log.action || '—'}
                        </span>
                      </td>
                      <td className="px-2 py-1 align-top">
                        {renderDetailsCell(log)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* GEO TAB */}
      {activeTab === 'geo' && (
        <>
          {/* Filters */}
          <div className="mb-3 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Date from
              </label>
              <input
                type="date"
                value={geoDateFrom}
                onChange={(e) => {
                  setGeoQuickRange('');
                  setGeoDateFrom(e.target.value);
                }}
                className="border rounded px-2 py-1 text-xs w-full"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Date to
              </label>
              <input
                type="date"
                value={geoDateTo}
                onChange={(e) => {
                  setGeoQuickRange('');
                  setGeoDateTo(e.target.value);
                }}
                className="border rounded px-2 py-1 text-xs w-full"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Role
              </label>
              <select
                value={geoRoleFilter}
                onChange={(e) => setGeoRoleFilter(e.target.value)}
                className="border rounded px-2 py-1 text-xs w-full"
              >
                {roles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Event type
              </label>
              <select
                value={geoType}
                onChange={(e) => setGeoType(e.target.value)}
                className="border rounded px-2 py-1 text-xs w-full"
              >
                {geoTypes.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-600 mb-1">
                Org ID (optional)
              </label>
              <div className="flex gap-1">
                <input
                  type="number"
                  value={geoOrgId}
                  onChange={(e) => setGeoOrgId(e.target.value)}
                  className="border rounded px-2 py-1 text-xs w-full"
                  placeholder="e.g. 1"
                />
                <button
                  type="button"
                  onClick={loadGeo}
                  className="px-3 py-1 rounded bg-blue-600 text-white text-xs"
                >
                  Apply
                </button>
              </div>
            </div>

            {/* QUICK RANGE dugmići */}
            <div className="md:col-span-5 flex flex-wrap items-center gap-2 mt-1">
              <span className="text-[11px] text-gray-500 mr-1">
                Quick range:
              </span>
              <button
                type="button"
                onClick={() => applyGeoQuickRange('today')}
                className={
                  'px-2 py-0.5 rounded-full border text-[11px] ' +
                  (geoQuickRange === 'today'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100')
                }
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => applyGeoQuickRange('last7')}
                className={
                  'px-2 py-0.5 rounded-full border text-[11px] ' +
                  (geoQuickRange === 'last7'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100')
                }
              >
                Last 7 days
              </button>
              <button
                type="button"
                onClick={() => applyGeoQuickRange('last30')}
                className={
                  'px-2 py-0.5 rounded-full border text-[11px] ' +
                  (geoQuickRange === 'last30'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100')
                }
              >
                Last 30 days
              </button>
            </div>
          </div>

          {errorGeo && (
            <div className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {errorGeo}
            </div>
          )}

          {loadingGeo ? (
            <div className="text-xs text-gray-600">Loading geolocation events…</div>
          ) : geoLogs.length === 0 ? (
            <div className="text-xs text-gray-500">
              No geolocation events for the selected filters.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-md overflow-auto max-h-[60vh]">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-1 text-left">Time</th>
                    <th className="px-2 py-1 text-left">Organization</th>
                    <th className="px-2 py-1 text-left">User</th>
                    <th className="px-2 py-1 text-left">Event</th>
                    <th className="px-2 py-1 text-left">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {geoLogs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-2 py-1 align-top text-[11px] text-gray-700 whitespace-nowrap">
                        {log.timestamp}
                      </td>
                      <td className="px-2 py-1 align-top text-[11px]">
                        <div className="font-semibold text-gray-800">
                          {log.organization_name || '—'}
                        </div>
                        {log.organization_id && (
                          <div className="text-[10px] text-gray-400">
                            ID: {log.organization_id}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1 align-top text-[11px]">
                        <div className="font-semibold text-gray-800">
                          {log.user_name || '—'}
                        </div>
                        <div className="text-[10px] text-gray-500">
                          {log.user_email || 'No email'}{' '}
                          {log.user_role && (
                            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                              {log.user_role}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1 align-top text-[11px]">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                          {log.type || '—'}
                        </span>
                      </td>
                      <td className="px-2 py-1 align-top text-[11px]">
                        {log.lat != null && log.lon != null ? (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openMapModal(log)}
                              className="p-1 rounded-full hover:bg-blue-50 text-blue-600"
                              title="Show on map"
                            >
                              <MapPin size={14} />
                            </button>
                            <span>
                              {Number(log.lat).toFixed(5)},{' '}
                              {Number(log.lon).toFixed(5)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400">
                            No coordinates
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* MAP MODAL (OpenStreetMap embed) */}
      {mapModalOpen && mapCoords.lat != null && mapCoords.lon != null && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center"
          onClick={closeMapModal}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-full max-w-3xl h-[80vh] max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <MapPin size={14} />
                Geolocation event map
              </h3>
              <button
                className="text-gray-500 hover:text-gray-800 text-lg"
                onClick={closeMapModal}
              >
                ✕
              </button>
            </div>

            <div className="flex-1 flex flex-col p-3 gap-2 text-xs text-gray-700">
              <div>
                {mapCoords.orgName && (
                  <div className="text-[11px] text-gray-500 mb-0.5">
                    Organization:{' '}
                    <span className="font-semibold">{mapCoords.orgName}</span>
                  </div>
                )}
                <div className="text-[11px] text-gray-500 mb-0.5">
                  User:{' '}
                  <span className="font-semibold">
                    {mapCoords.userName || '—'}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 mb-0.5">
                  Time: <span className="font-mono">{mapCoords.timestamp}</span>
                  {mapCoords.type && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                      {mapCoords.type}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500">
                  Lat/Lon:{' '}
                  <span className="font-mono">
                    {mapCoords.lat.toFixed(5)}, {mapCoords.lon.toFixed(5)}
                  </span>
                </div>
              </div>

              <div className="flex-1 border rounded overflow-hidden">
                {embedUrl && (
                  <iframe
                    title="OpenStreetMap location"
                    src={embedUrl}
                    className="w-full h-full"
                    style={{ border: 0 }}
                    loading="lazy"
                  />
                )}
              </div>

              <div className="text-[11px] text-gray-500 flex justify-between items-center">
                <span>
                  Map data ©{' '}
                  <a
                    href="https://www.openstreetmap.org/copyright"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    OpenStreetMap contributors
                  </a>
                  .
                </span>
                {fullMapUrl && (
                  <a
                    href={fullMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Open full map in new tab
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAuditLog;



